/// <amd-module name="Test/Analytics/TestResultsGrid/Store/TestResultsTreeStore" />
import { ReportConfigurationDefinitions } from "Test/Analytics/Common/Definitions";
import * as CommonTypes from "Test/Analytics/Common/Types";
import { ConfigurationUtility } from "Test/Analytics/Common/Utils/ConfigurationUtility";
import { StringFormattingUtility } from "Test/Analytics/Common/Utils/StringFormattingUtility";
import * as TcmPerfScenarios from "Test/Analytics/Common/Utils/TcmPerfScenarios";
import * as TcmTelemetryConstants from "Test/Analytics/Common/Utils/TcmTelemetryConstants";
import { publishEvent } from "Test/Analytics/Common/Utils/TelemetryService";
import { TestResultsGridActions } from "Test/Analytics/TestResultsGrid/Actions/TestResultsGridActions";
import { DetailedListSourceManager } from "Test/Analytics/TestResultsGrid/DetailedListSourceManager";
import * as Resources from "Test/Analytics/TestResultsGrid/Resources";
import { DetailedTestListDefaultSource } from "Test/Analytics/TestResultsGrid/Source/DetailedTestListDefaultSource";
import { DurationFormatter } from "Test/Analytics/TestResultsGridCommon/DurationFormatter";
import { IDetailedTestListSource } from "Test/Analytics/TestResultsGridCommon/Types";
import * as CommonUtils from "Test/common-util/Common.Utils";
import { FilterMetadataActions } from "Test/MetadataHandler/Actions/FilterMetadataActions";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { Store } from "VSS/Reflux/Store";
import { ITreeItem, ITreeItemProvider, ITreeListItem } from "VSSUI/Tree";

declare type GetChildrenFunction = (
    item: CommonTypes.IDetailedListItem
) => CommonTypes.IDetailedListItem[] | Promise<CommonTypes.IDetailedListItem[]>;
declare type GetTreeItemFunction = (item: CommonTypes.IDetailedListItem) => ITreeItem<CommonTypes.IDetailedListItem>;

export interface IDetailedTestListState {
    sortedColumn: CommonTypes.IDetailedListColumn;
    items: CommonTypes.IDetailedListItem[] | null;
    gridViewDisplayType: CommonTypes.GridViewDisplayType;
    totalTestResults: number | null;
    treeItemProvider: ITreeItemProvider<CommonTypes.IDetailedListItem>;
}

export class TestResultsTreeItemProvider implements ITreeItemProvider<CommonTypes.IDetailedListItem> {
    private _getChildren: GetChildrenFunction;
    private _getTreeItem: GetTreeItemFunction;
    private _map: Map<CommonTypes.IDetailedListItem, ITreeListItem<CommonTypes.IDetailedListItem>>;

    constructor(
        getChildren: GetChildrenFunction,
        getTreeItem: GetTreeItemFunction,
        map: Map<CommonTypes.IDetailedListItem, ITreeListItem<CommonTypes.IDetailedListItem>>
    ) {
        this._getChildren = getChildren;
        this._getTreeItem = getTreeItem;
        this._map = map;
    }

    public getChildren(item: CommonTypes.IDetailedListItem): CommonTypes.IDetailedListItem[] | Promise<CommonTypes.IDetailedListItem[]> {
        return this._getChildren(item);
    }

    public getTreeItem(item: CommonTypes.IDetailedListItem): ITreeItem<CommonTypes.IDetailedListItem> {
        return this._getTreeItem(item);
    }

    public setTreeListItem(item: CommonTypes.IDetailedListItem, treeListItem: ITreeListItem<CommonTypes.IDetailedListItem>): void {
        this._map.set(item, treeListItem);
    }

    public getTreeListItem(item: CommonTypes.IDetailedListItem): ITreeListItem<CommonTypes.IDetailedListItem> {
        return this._map.get(item) || (undefined as any);
    }
}

export class DetailedTestListStore extends Store {
    private _treeListItemMap: Map<CommonTypes.IDetailedListItem, ITreeListItem<CommonTypes.IDetailedListItem>> = new Map();
    private _stageIdToNameMap: { [key: string]: string };

    // this is first list which will be rendered in the tree, it may be group list or the testcaselist in case of group none
    private _initialList: CommonTypes.IDetailedListItem[] | null;

    private _groupItemKeyToDetailMap: { [key: string]: CommonTypes.IDetailedListGroupItem };
    private _state: IDetailedTestListState;
    private _itemList: CommonTypes.IDetailedListData;
    private _confValues: CommonTypes.IReportConfiguration;
    private _showMoreKey: string = "ShowMoreKey";
    private _perfService: IVssPerformanceService;

    public constructor(
        private _actions: TestResultsGridActions,
        private _pageContext: IVssPageContext,
        private _testReportContext: CommonTypes.ITestReportContext,
        private _filterMetadataActions: FilterMetadataActions
    ) {
        super("");
        this._initialize(this._actions);

        this._perfService = this._pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
    }

    public dispose(): void {
        this._actions.updateReportsWithConfigurationAction.removeListener(this._onUpdateConfiguration);
        this._actions.updateDetailedTestListAction.removeListener(this._onUpdateDetailedTestList);
        this._actions.detailedTestListColumnOrderChangedAction.removeListener(this._onColumnOrderChanged);
        this._actions.detailedTestListSortByColumnAction.removeListener(this._onSortByColumn);
        this._actions.updateTestOutcomeMetricsAction.removeListener(this._onUpdateTestOutcomeMetrics);
        this._actions.initializingTestResultsAction.removeListener(this._showInitializingTestListExperience);
        this._actions.detailedListShowMoreAction.removeListener(this._onDetailedListShowMore);
        this._actions.detailedListShowMoreInsideGroupAction.removeListener(this._onDetailedListShowMoreInsideGroup);
        this._filterMetadataActions.updateStageIdToNameMapAction.removeListener(this._onUpdateStageIdToNameMap);
    }

    public getTreeItemProvider(): ITreeItemProvider<CommonTypes.IDetailedListItem> {
        return new TestResultsTreeItemProvider(this._getChildren, this._getTreeItem, this._treeListItemMap);
    }

    public getState(): IDetailedTestListState {
        return this._state;
    }

    public getConfValue(): CommonTypes.IReportConfiguration {
        return this._confValues;
    }

    public getTestContext(item: CommonTypes.IDetailedListItem): CommonTypes.ITestContext | null {
        if (item.nodeType === CommonTypes.TreeNodeType.leaf) {
            return {
                testIdentifier: item.itemkey as string,
                testName: item.itemName
            } as CommonTypes.ITestContext;
        }

        return null;
    }

    public getNextPageToken(parentKey: number | string | undefined): CommonTypes.INextDataPageToken | undefined {
        switch (this._confValues.groupBy) {
            case CommonTypes.GroupBy.None:
                return this._itemList.nextPageToken;
            default:
                // for all groupBy
                if (parentKey || parentKey === "") {
                    return this._groupItemKeyToDetailMap[parentKey.toString()].nextPageToken;
                } else {
                    return this._itemList.nextPageToken;
                }
        }
    }

    public isListCompletelyFetched(parentKey?: number | string): boolean {
        if (parentKey || parentKey === "") {
            // for groupBy's
            const parentKeyAsStr: string = parentKey.toString();
            if (
                this._groupItemKeyToDetailMap &&
                this._groupItemKeyToDetailMap[parentKeyAsStr] &&
                this._groupItemKeyToDetailMap[parentKeyAsStr].children.length > 0
            ) {
                const temp = this._groupItemKeyToDetailMap[parentKeyAsStr].nextPageToken;
                return !(temp && temp.token);
            }
            return false;
        }
        return !(this._itemList.nextPageToken && this._itemList.nextPageToken.token);
    }

    private _initialize(actions: TestResultsGridActions): void {
        const reportConfigurationDef = new ReportConfigurationDefinitions();
        this._state = {
            sortedColumn: reportConfigurationDef.defaultDetailedTestListSortedColumn,
            gridViewDisplayType: CommonTypes.GridViewDisplayType.Loading
        } as IDetailedTestListState;
        this._itemList = { detailedListItems: [] } as CommonTypes.IDetailedListData;
        this._initialList = [];

        this._actions.updateReportsWithConfigurationAction.addListener(this._onUpdateConfiguration);
        this._actions.updateDetailedTestListAction.addListener(this._onUpdateDetailedTestList);
        this._actions.detailedTestListColumnOrderChangedAction.addListener(this._onColumnOrderChanged);
        this._actions.detailedTestListSortByColumnAction.addListener(this._onSortByColumn);
        this._actions.updateTestOutcomeMetricsAction.addListener(this._onUpdateTestOutcomeMetrics);
        this._actions.initializingTestResultsAction.addListener(this._showInitializingTestListExperience);
        this._actions.detailedListShowMoreAction.addListener(this._onDetailedListShowMore);
        this._actions.detailedListShowMoreInsideGroupAction.addListener(this._onDetailedListShowMoreInsideGroup);
        this._filterMetadataActions.updateStageIdToNameMapAction.addListener(this._onUpdateStageIdToNameMap);
    }

    private _getChildren: GetChildrenFunction = (item: CommonTypes.IDetailedListItem) => {
        if (!item) {
            if (!this._confValues || !this._initialList) {
                return [];
            }
            return this._initialList;
        }
        if (this._groupItemKeyToDetailMap) {
            return this._getDetailedListGroupExpandedItems(item);
        }

        return [];
    };

    private _getTreeItem: GetTreeItemFunction = (item: CommonTypes.IDetailedListItem) => {
        return {
            isExpandable: item.nodeType === CommonTypes.TreeNodeType.group ? true : false,
            id: item.itemkey ? item.itemkey.toString() : "",
            defaultIsExpanded: item.expanded,
            item: item
        };
    };

    private _onSortByColumn = () => {
        this._state.gridViewDisplayType = CommonTypes.GridViewDisplayType.Initialized;

        if (this._itemList && this._itemList.detailedListItems && this._itemList.detailedListItems.length > 0) {
            this._sortItemsInList(this._itemList.detailedListItems, this._state.sortedColumn);

            // collapse all expanded groups as data is sorted and one should expand again to view new sorted list.
            if (this._groupItemKeyToDetailMap) {
                for (const key in this._groupItemKeyToDetailMap) {
                    this._groupItemKeyToDetailMap[key].groupItem.expanded = false;
                }
            }

            this._initialList = this._createItemListToShow();

            this.emitChanged();
        }
    };

    private _onUpdateConfiguration = (confValues: CommonTypes.IReportConfiguration) => {
        this._state.gridViewDisplayType = CommonTypes.GridViewDisplayType.Loading;
        this._state.totalTestResults = null;
        this._confValues = { ...confValues };
        this._state.items = null;
        this._itemList = { detailedListItems: [] } as CommonTypes.IDetailedListData;

        this._updateColumnToOrderBy(this._confValues);

        this.emitChanged();
    };

    // This method is for show more in both plain test list or grouped list.
    private _onDetailedListShowMore = (detailedListItemsData: CommonTypes.IDetailedListData) => {
        if (this._confValues && !ConfigurationUtility.areConfValuesEqual(this._confValues, detailedListItemsData.confValues)) {
            // Do not update store as the configuration has changed.
            return;
        }

        this._itemList.detailedListItems.push(...(detailedListItemsData.detailedListItems || []));
        this._itemList.nextPageToken = detailedListItemsData.nextPageToken;

        this._initialList = this._createItemListToShow();

        this.emitChanged();
    };

    private _updateColumnToOrderBy(confValues: CommonTypes.IReportConfiguration): void {
        const currentOrderedColumn: CommonTypes.IDetailedListColumn = this._state.sortedColumn;
        const columns: CommonTypes.ColumnIndices[] = [
            CommonTypes.ColumnIndices.Passrate,
            CommonTypes.ColumnIndices.AvgDuration,
            CommonTypes.ColumnIndices.TotalCount
        ];

        if (confValues && confValues.outcomes && confValues.outcomes.length > 0) {
            for (const outcome of confValues.outcomes) {
                const columnIndices = this._getColumnFromOutcome(outcome);
                if (columnIndices) {
                    columns.push(columnIndices);
                }
            }
        }

        if (columns.indexOf(currentOrderedColumn.column) === -1) {
            if (columns.indexOf(CommonTypes.ColumnIndices.FailedCount) !== -1) {
                currentOrderedColumn.column = CommonTypes.ColumnIndices.FailedCount;
                currentOrderedColumn.sortOrder = CommonTypes.SortOrder.Descending;
            } else {
                currentOrderedColumn.column = CommonTypes.ColumnIndices.Passrate;
                currentOrderedColumn.sortOrder = CommonTypes.SortOrder.Ascending;
            }
        }
    }

    private _onUpdateStageIdToNameMap = (stageIdToNameMap: { [key: string]: string }) => {
        this._stageIdToNameMap = { ...stageIdToNameMap };
        if (this._itemList && this._itemList.detailedListItems && this._itemList.detailedListItems.length) {
            this._initialList = this._createItemListToShow();
            this.emitChanged();
        }
    };

    private _getColumnFromOutcome(outcome: CommonTypes.TestOutcome): CommonTypes.ColumnIndices | undefined {
        switch (outcome) {
            case CommonTypes.TestOutcome.Failed:
                return CommonTypes.ColumnIndices.FailedCount;

            case CommonTypes.TestOutcome.Passed:
                return CommonTypes.ColumnIndices.PassedCount;

            case CommonTypes.TestOutcome.Inconclusive:
                return CommonTypes.ColumnIndices.InconclusiveCount;

            case CommonTypes.TestOutcome.Aborted:
                return CommonTypes.ColumnIndices.AbortedCount;

            case CommonTypes.TestOutcome.NotExecuted:
                return CommonTypes.ColumnIndices.NotExecutedCount;

            case CommonTypes.TestOutcome.Error:
                return CommonTypes.ColumnIndices.ErrorCount;

            case CommonTypes.TestOutcome.NotImpacted:
                return CommonTypes.ColumnIndices.NotImpactedCount;
        }
    }

    private _onUpdateDetailedTestList = (detailedListItemsData: CommonTypes.IDetailedListData) => {
        if (this._confValues && !ConfigurationUtility.areConfValuesEqual(this._confValues, detailedListItemsData.confValues)) {
            // Do not update store as the configuration has changed.
            return;
        }

        this._state.gridViewDisplayType = CommonTypes.GridViewDisplayType.Initialized;

        this._itemList = {
            detailedListItems: detailedListItemsData.detailedListItems || [],
            nextPageToken: detailedListItemsData.nextPageToken
        } as CommonTypes.IDetailedListData;
        this._groupItemKeyToDetailMap = {};

        if (!(this._confValues.groupBy === CommonTypes.GroupBy.Environment) || this._stageIdToNameMap) {
            this._initialList = this._createItemListToShow();
        }
        this.emitChanged();
    };

    private _createItemListToShow(): CommonTypes.IDetailedListItem[] {
        switch (this._confValues.groupBy) {
            case CommonTypes.GroupBy.None: // Plain test list
                this._state.items = this._itemList.detailedListItems.map(i => {
                    return this._prepareItemToDisplay(i, CommonTypes.TreeNodeType.leaf, 0);
                });

                if (this._itemList.nextPageToken && this._itemList.nextPageToken.token) {
                    this._state.items.push({
                        itemkey: this._showMoreKey,
                        itemName: Resources.ShowMoreText,
                        nodeType: CommonTypes.TreeNodeType.showMore,
                        depth: 0
                    } as CommonTypes.IDetailedListItem);
                }
                return this._state.items;
            case CommonTypes.GroupBy.Environment:
                // TODO : Revisit On this when required
                // Query RM to get the data, this can be done in n async.

                this._itemList.detailedListItems.forEach(item => {
                    // Replace Environment Id with Environment Name
                    item.itemName =
                        item.itemkey && this._stageIdToNameMap && this._stageIdToNameMap[item.itemkey.toString()]
                            ? this._stageIdToNameMap[item.itemkey.toString()]
                            : StringFormattingUtility.getDeletedEnvironmentDefIdDisplayString(item.itemkey.toString());
                });
                return this._prepareItemListFromGroupsList();

            default:
                // For all group by's
                return this._prepareItemListFromGroupsList();
        }
    }

    private _prepareItemToDisplay(
        item: CommonTypes.IDetailedListItem,
        nodeType: CommonTypes.TreeNodeType,
        depth: number
    ): CommonTypes.IDetailedListItem {
        const newItem: CommonTypes.IDetailedListItem = { ...item };

        newItem.nodeType = nodeType;
        newItem.depth = depth;
        newItem.passPercentage = CommonUtils.TestReportDataParser.getPercentageInDisplayFormat(item.passPercentage as number);
        newItem.avgDuration = DurationFormatter.getDurationInAbbreviatedFormat(item.avgDuration as number);
        newItem.avgDurationAriaLabel = DurationFormatter.getDurationAriaLabel(item.avgDuration as number);

        return newItem;
    }

    private _showInitializingTestListExperience = () => {
        if (this._state.gridViewDisplayType === CommonTypes.GridViewDisplayType.Loading) {
            this._state.gridViewDisplayType = CommonTypes.GridViewDisplayType.Initializing;
            this.emitChanged();
        }
    };

    private _getDetailedListGroupExpandedItems(
        groupItem: CommonTypes.IDetailedListItem
    ): CommonTypes.IDetailedListItem[] | Promise<CommonTypes.IDetailedListItem[]> {
        // let confValues = this._configToolbarStore.getState().reportConfigurationValues;

        this._perfService.startScenario(TcmPerfScenarios.TestAX_ResultsGridElement, false);

        if (this.isListCompletelyFetched(groupItem.itemkey)) {
            this._perfService.endScenario(TcmPerfScenarios.Area, TcmPerfScenarios.TestAX_ResultsGridElement, false);
            // If list is completely fetched within group then sort can be done in-place rather making server call.
            return this._getGroupExpandAndSortByColumn(groupItem);
        } else {
            if (
                this._groupItemKeyToDetailMap &&
                this._groupItemKeyToDetailMap[groupItem.itemkey] &&
                this._groupItemKeyToDetailMap[groupItem.itemkey].children.length > 0
            ) {
                return this._prepareItemListForGroupChildren(this._groupItemKeyToDetailMap[groupItem.itemkey].children, groupItem.itemkey);
            }

            const detailedListSourceManager = new DetailedListSourceManager(this._pageContext);

            const promise = new Promise<CommonTypes.IDetailedListItem[]>((resolve, reject) => {
                detailedListSourceManager.getDetailedListSourceManager().then((detailedListSource: IDetailedTestListSource) => {
                    detailedListSource
                        .queryDetailedList(this._testReportContext, this._confValues, this._state.sortedColumn, null, groupItem.itemkey)
                        .then(
                            (detailedListData: CommonTypes.IDetailedListData) => {
                                const detailedListGroupData: CommonTypes.IDetailedListGroupData = {
                                    detailedListGroupItem: {
                                        groupItem: groupItem,
                                        children: detailedListData.detailedListItems,
                                        nextPageToken: detailedListData.nextPageToken
                                    } as CommonTypes.IDetailedListGroupItem,
                                    confValues: detailedListData.confValues
                                } as CommonTypes.IDetailedListGroupData;

                                this._perfService.endScenario(TcmPerfScenarios.Area, TcmPerfScenarios.TestAX_ResultsGridElement, false);

                                const filtersApplied: string[] = this._confValues.configuredFilters
                                    ? Object.keys(this._confValues.configuredFilters)
                                    : [];
                                const telemetryData: { [key: string]: string | number } = {
                                    [TcmTelemetryConstants.SortBy]: CommonTypes.ColumnIndices[this._state.sortedColumn.column].toString(),
                                    [TcmTelemetryConstants.GroupBy]: CommonTypes.GroupBy[this._confValues.groupBy].toString(),
                                    [TcmTelemetryConstants.Duration]: CommonTypes.Period[this._confValues.period].toString(),
                                    [TcmTelemetryConstants.Rows]: detailedListData.detailedListItems.length,
                                    [TcmTelemetryConstants.IsCached]: String(detailedListData.isCachedData),
                                    [TcmTelemetryConstants.Filters]: filtersApplied.length ? filtersApplied.toString() : TcmTelemetryConstants.Clear,
                                    [TcmTelemetryConstants.FiltersCount]: filtersApplied.length
                                };

                                publishEvent(
                                    this._pageContext,
                                    TcmTelemetryConstants.featureTestAX_ResultsGridElement,
                                    telemetryData,
                                    this._testReportContext,
                                    this._confValues
                                );

                                resolve(this._getGroupExpanded(detailedListGroupData));
                            },
                            (error: Error) => {
                                reject(error);
                            }
                        );
                });
            });

            return promise;
        }
    }

    private _getGroupExpanded = (groupItemExpanded: CommonTypes.IDetailedListGroupData) => {
        if (this._confValues && !ConfigurationUtility.areConfValuesEqual(this._confValues, groupItemExpanded.confValues)) {
            // do not update store as the configuration has changed.
            return [];
        }
        const itemKey = groupItemExpanded.detailedListGroupItem.groupItem.itemkey;
        if (this._groupItemKeyToDetailMap[itemKey]) {
            this._groupItemKeyToDetailMap[itemKey].groupItem.expanded = true;

            // this is to ensure that we don't update a group which was first expanded, then show more,
            // then collapsed and then again expanded.
            if (!this._groupItemKeyToDetailMap[itemKey].children || this._groupItemKeyToDetailMap[itemKey].children.length === 0) {
                this._groupItemKeyToDetailMap[itemKey].children = groupItemExpanded.detailedListGroupItem.children
                    ? groupItemExpanded.detailedListGroupItem.children
                    : [];
                this._groupItemKeyToDetailMap[itemKey].nextPageToken = groupItemExpanded.detailedListGroupItem.nextPageToken;
            }
        }

        return this._prepareItemListForGroupChildren(this._groupItemKeyToDetailMap[itemKey].children, itemKey);
    };

    private _onColumnOrderChanged = (orderedColumn: CommonTypes.IDetailedListColumn) => {
        if (this._itemList.detailedListItems.length > 0) {
            this._state.gridViewDisplayType = CommonTypes.GridViewDisplayType.Loading;
            this._state.sortedColumn = { ...orderedColumn };
            this._state.items = null;
            this._initialList = [];
            this.emitChanged();
        }
    };

    private _getGroupExpandAndSortByColumn = (groupItem: CommonTypes.IDetailedListItem) => {
        if (this._groupItemKeyToDetailMap[groupItem.itemkey]) {
            // expand group.
            this._groupItemKeyToDetailMap[groupItem.itemkey].groupItem.expanded = true;

            this._sortItemsInList(this._groupItemKeyToDetailMap[groupItem.itemkey].children, this._state.sortedColumn);
            return this._prepareItemListForGroupChildren(this._groupItemKeyToDetailMap[groupItem.itemkey].children, groupItem.itemkey);
        }

        const telemetryData: { [key: string]: string | number } = {
            [TcmTelemetryConstants.SortBy]: CommonTypes.ColumnIndices[this._state.sortedColumn.column].toString(),
            [TcmTelemetryConstants.GroupBy]: CommonTypes.GroupBy[this._confValues.groupBy].toString(),
            [TcmTelemetryConstants.Duration]: CommonTypes.Period[this._confValues.period].toString()
        };

        publishEvent(
            this._pageContext,
            TcmTelemetryConstants.featureTestAX_ResultsGridElement,
            telemetryData,
            this._testReportContext,
            this._confValues
        );

        return [];
    };

    private _onUpdateTestOutcomeMetrics = (metricsData: CommonTypes.ICardMetricsData) => {
        if (this._confValues && !ConfigurationUtility.areConfValuesEqual(this._confValues, metricsData.confValues)) {
            // do not update store as the configuration has changed.
            return;
        }

        // save total tests to be displayed when loading Test Results take more than 5 minutes.
        this._state.totalTestResults = metricsData ? metricsData.totalCount : null;
    };

    // this method is for show more inside a group.
    private _onDetailedListShowMoreInsideGroup = (showMoreData: CommonTypes.IDetailedListGroupData) => {
        if (this._confValues && !ConfigurationUtility.areConfValuesEqual(this._confValues, showMoreData.confValues)) {
            // do not update store as the configuration has changed.
            return;
        }

        this._groupItemKeyToDetailMap[showMoreData.detailedListGroupItem.groupItem.itemkey].children.push(
            ...showMoreData.detailedListGroupItem.children
        );
        this._groupItemKeyToDetailMap[showMoreData.detailedListGroupItem.groupItem.itemkey].nextPageToken =
            showMoreData.detailedListGroupItem.nextPageToken;
        this.emitChanged();
    };

    private _prepareItemListForGroupChildren(
        detailListItems: CommonTypes.IDetailedListItem[],
        itemKey: string | number
    ): CommonTypes.IDetailedListItem[] {
        const display_items: CommonTypes.IDetailedListItem[] = [];
        detailListItems.forEach(item => {
            display_items.push(this._prepareItemToDisplay(item, CommonTypes.TreeNodeType.leaf, 1));
        });
        const nextPageToken = this._groupItemKeyToDetailMap[itemKey].nextPageToken;
        if (nextPageToken && nextPageToken.token) {
            display_items.push({
                itemkey: this._showMoreKey,
                itemName: Resources.ShowMoreText,
                nodeType: CommonTypes.TreeNodeType.showMore,
                parentItemKey: itemKey,
                depth: 1
            } as CommonTypes.IDetailedListItem);
        }
        return display_items;
    }

    private _prepareItemListFromGroupsList(): CommonTypes.IDetailedListItem[] {
        const display_items: CommonTypes.IDetailedListItem[] = [];
        this._itemList.detailedListItems.forEach(item => {
            const itemKey: string | number = item.itemkey;

            if (!this._groupItemKeyToDetailMap[itemKey]) {
                this._groupItemKeyToDetailMap[itemKey] = { groupItem: item, children: [] };
            }

            // inserting group item.
            display_items.push(this._prepareItemToDisplay(this._groupItemKeyToDetailMap[itemKey].groupItem, CommonTypes.TreeNodeType.group, 0));
        });

        if (this._itemList.nextPageToken && this._itemList.nextPageToken.token) {
            display_items.push({
                itemkey: this._showMoreKey,
                itemName: Resources.ShowMoreText,
                nodeType: CommonTypes.TreeNodeType.showMore,
                depth: 0
            } as CommonTypes.IDetailedListItem);
        }
        this._state.items = display_items;
        return display_items;
    }

    private _sortItemsInList(items: CommonTypes.IDetailedListItem[], sortedColumn: CommonTypes.IDetailedListColumn): void {
        items.sort((item1: CommonTypes.IDetailedListItem, item2: CommonTypes.IDetailedListItem) => {
            let diff = 0;
            switch (sortedColumn.column) {
                case CommonTypes.ColumnIndices.Passrate:
                    diff = (item1.passPercentage as number) - (item2.passPercentage as number);
                    break;
                case CommonTypes.ColumnIndices.TotalCount:
                    diff = item1.totalCount - item2.totalCount;
                    break;
                case CommonTypes.ColumnIndices.AvgDuration:
                    diff = (item1.avgDuration as number) - (item2.avgDuration as number);
                    break;
                case CommonTypes.ColumnIndices.FailedCount:
                    diff = item1.failedCount - item2.failedCount;
                    break;
                case CommonTypes.ColumnIndices.PassedCount:
                    diff = item1.passedCount - item2.passedCount;
                    break;
                case CommonTypes.ColumnIndices.NotExecutedCount:
                    diff = item1.notExecutedCount - item2.notExecutedCount;
                    break;
                case CommonTypes.ColumnIndices.NotImpactedCount:
                    diff = item1.notImpactedCount - item2.notImpactedCount;
                    break;
                case CommonTypes.ColumnIndices.ErrorCount:
                    diff = item1.errorCount - item2.errorCount;
                    break;
                case CommonTypes.ColumnIndices.InconclusiveCount:
                    diff = item1.inconclusiveCount - item2.inconclusiveCount;
                    break;
                case CommonTypes.ColumnIndices.AbortedCount:
                    diff = item1.abortedCount - item2.abortedCount;
                    break;
                case CommonTypes.ColumnIndices.FlakyCount:
                    diff = item1.flakyCount - item2.flakyCount;
            }

            return sortedColumn.sortOrder === CommonTypes.SortOrder.Descending ? -1 * diff : diff;
        });
    }
}
